import { useMemo } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Access mode for server provisioning and management routes:
 *
 * - "full"            — Full access; user has an active paid subscription.
 * - "trial_view_only" — Free trial user. May view server data but cannot
 *                       provision new servers or modify configs.
 * - "expired"         — Free trial has expired. Should redirect to /pricing.
 * - "no_access"       — No subscription and no trial. Redirect to /pricing.
 */
export type ServerProvisioningMode =
  | "full"
  | "trial_view_only"
  | "expired"
  | "no_access";

export type ServerProvisioningGateResult = {
  /** True while Clerk auth or Convex queries are still resolving */
  isLoading: boolean;
  /**
   * Current access mode. Drives gate behaviour:
   * - "full"            → render children normally
   * - "trial_view_only" → render children with view-only banner + disabled actions
   * - "expired"         → redirect to /pricing?reason=trial_expired
   * - "no_access"       → redirect to /pricing?reason=no_subscription
   */
  mode: ServerProvisioningMode;
  /** Maximum number of server slots available to the user right now */
  serverSlotLimit: number;
  /**
   * Days remaining on the free trial. null when not on a free trial.
   * 0 when the trial has expired (mode will be "expired" in that case).
   */
  trialDaysRemaining: number | null;
  /** When the free trial ends (ms epoch). null when not on a free trial. */
  trialEndsAt: number | null;
  /**
   * True when the user is on a self-service free trial (no Polar billing ID).
   * false for Polar-backed trials and active paid subscriptions.
   */
  isFreeTrial: boolean;
  /** Current subscription tier ("free" | "starter" | "plus" | "pro" | "enterprise") */
  tier: string;
  /** Current billing status ("active" | "trialing" | "canceled" | "past_due" | "none") */
  status: string;
  /** Where to redirect if mode is "expired" or "no_access" */
  redirectPath: string;
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * useServerProvisioningGate
 *
 * Evaluates whether the current user may access server provisioning and
 * management features, and at what level (full vs view-only).
 *
 * Data source: `billing.getServerProvisioningEntitlement` (Convex query).
 * The Convex function is the single source of truth for this decision so that
 * the gate is always consistent with the server-side billing state.
 *
 * @param nextPath - The current route path, used to build post-login redirect URLs.
 */
export function useServerProvisioningGate(
  nextPath: string
): ServerProvisioningGateResult {
  const { isLoaded, isSignedIn } = useAuth();

  const entitlement = useQuery(
    api.billing.getServerProvisioningEntitlement,
    isSignedIn ? {} : "skip"
  );

  return useMemo<ServerProvisioningGateResult>(() => {
    // ── Loading ────────────────────────────────────────────────────────────
    if (!isLoaded || (isSignedIn && entitlement === undefined)) {
      return {
        isLoading: true,
        mode: "no_access",
        serverSlotLimit: 0,
        trialDaysRemaining: null,
        trialEndsAt: null,
        isFreeTrial: false,
        tier: "free",
        status: "none",
        redirectPath: buildPricingRedirect("auth_required", nextPath),
      };
    }

    // ── Unauthenticated ────────────────────────────────────────────────────
    if (!isSignedIn || entitlement === null || entitlement === undefined) {
      return {
        isLoading: false,
        mode: "no_access",
        serverSlotLimit: 0,
        trialDaysRemaining: null,
        trialEndsAt: null,
        isFreeTrial: false,
        tier: "free",
        status: "none",
        redirectPath: buildPricingRedirect("auth_required", nextPath),
      };
    }

    // ── Map Convex result to gate result ───────────────────────────────────
    const mode = entitlement.mode as ServerProvisioningMode;

    const redirectPath = (() => {
      if (mode === "expired") return buildPricingRedirect("trial_expired", nextPath);
      if (mode === "no_access") return buildPricingRedirect("no_subscription", nextPath);
      return "";
    })();

    return {
      isLoading: false,
      mode,
      serverSlotLimit: entitlement.serverSlotLimit,
      trialDaysRemaining: entitlement.trialDaysRemaining,
      trialEndsAt: entitlement.trialEndsAt,
      isFreeTrial: entitlement.isFreeTrial,
      tier: entitlement.tier,
      status: entitlement.status,
      redirectPath,
    };
  }, [isLoaded, isSignedIn, entitlement, nextPath]);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function buildPricingRedirect(reason: string, next: string): string {
  const q = new URLSearchParams({
    upgrade: "1",
    reason,
    next,
    source: "server_provisioning_gate",
  });
  return `/pricing?${q.toString()}`;
}
